import { useState } from 'react'

import {
  useTagOptimizationApplyApi,
  useTagOptimizationDismissApi,
  useTagOptimizationListApi,
  useTagOptimizationRunApi
} from '@/common/api/modules/searchTag'
import {
  TagOptimization,
  TagOptimizationAction,
  TagOptimizationKind,
  TagOptimizationStatus
} from '@/common/api/params/searchTag'
import { searchTagCandidatesPath, searchTagListPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

const STATUS_TABS: { key: TagOptimizationStatus; label: string }[] = [
  { key: 'pending', label: '未処理' },
  { key: 'applied', label: '適用済み' },
  { key: 'dismissed', label: '見送り' }
]

const KIND_LABEL: Record<TagOptimizationKind, string> = {
  band_deviation: '母集団の逸脱',
  low_ctr: 'CTRが低い',
  no_traffic: '流入なし',
  thin_tagging: 'タグ付けが薄い'
}

const ACTION_LABEL: Record<TagOptimizationAction, string> = {
  rebuild: '母集団を作り直す',
  merge: '統合を検討する',
  rename: '表示名を見直す',
  noindex: '検索インデックスから外す',
  extract: '候補抽出を実行する'
}

/** 適用ボタンで実行できる手。rename / merge は変更内容の判断が要るため画面へ誘導する */
const APPLICABLE: TagOptimizationAction[] = ['rebuild', 'noindex', 'extract']

const detailText = (o: TagOptimization): string => {
  const d = o.detail || {}
  const parts: string[] = []
  const push = (label: string, key: string, unit = '') => {
    const v = d[key]
    if (v !== undefined && v !== null) parts.push(`${label} ${v}${unit}`)
  }
  switch (o.kind) {
    case 'band_deviation':
      push('現在', 'after', '件')
      push('目安下限', 'band_min', '件')
      push('目安上限', 'band_max', '件')
      if (d.rebuilt) parts.push('自動で作り直し済み')
      break
    case 'low_ctr':
      push('CTR', 'ctr', '%')
      push('中央値', 'median', '%')
      push('表示', 'impressions', '回')
      push('クリック', 'clicks', '回')
      break
    case 'no_traffic':
      push('対象期間', 'days', '日')
      push('表示', 'impressions', '回')
      parts.push('タグページ表示 0 回')
      break
    case 'thin_tagging':
      push('対象商品', 'thin', '件')
      push('全商品', 'total', '件')
      push('比率', 'ratio', '%')
      break
  }
  return parts.join(' / ')
}

const alternatives = (o: TagOptimization): string[] => {
  const v = o.detail?.alternatives
  return Array.isArray(v) ? (v as string[]) : []
}

const Row = ({
  o,
  siteId,
  onApply,
  onDismiss,
  acting
}: {
  o: TagOptimization
  siteId: number
  onApply: (id: number) => void
  onDismiss: (id: number) => void
  /** この行の実行中の操作。SWR の isMutating はフック単位（＝全行で共有）なので使わない */
  acting: 'apply' | 'dismiss' | null
}) => {
  const alts = alternatives(o)
  const canApply = APPLICABLE.includes(o.action)
  return (
    <div className='border-border-input flex flex-col gap-2 border-b py-3 last:border-b-0'>
      <div className='flex flex-wrap items-center gap-2'>
        <span className='bg-surface-emphasis text-emphasis rounded px-2 py-0.5 text-[11px] font-semibold'>
          {KIND_LABEL[o.kind]}
        </span>
        <Text size={14} weight='bold'>
          {o.summary}
        </Text>
      </div>
      <Text size={12} color={TextColor.SECONDARY}>
        {detailText(o)}
      </Text>
      {alts.length > 0 && (
        <Text size={12} color={TextColor.SECONDARY}>
          言い換えの候補（同義語より）: {alts.join(' / ')}
        </Text>
      )}
      {o.status === 'pending' && (
        <div className='flex flex-wrap items-center gap-2'>
          {canApply ? (
            <Button
              color={ButtonColor.PRIMARY}
              loading={acting === 'apply'}
              disabled={acting !== null}
              onClick={() => onApply(o.id)}
            >
              {ACTION_LABEL[o.action]}
            </Button>
          ) : (
            <>
              <Button
                color={ButtonColor.SECONDARY}
                link={
                  o.tag_id
                    ? `${searchTagListPath(siteId)}?search=${encodeURIComponent(o.tag_name ?? '')}`
                    : searchTagListPath(siteId)
                }
              >
                タグ一覧で開く
              </Button>
              <Text size={12} color={TextColor.SECONDARY}>
                {ACTION_LABEL[o.action]}（変更内容はタグ一覧で決めます）
              </Text>
            </>
          )}
          <Button
            color={ButtonColor.SECONDARY}
            loading={acting === 'dismiss'}
            disabled={acting !== null}
            onClick={() => onDismiss(o.id)}
          >
            見送る
          </Button>
        </div>
      )}
    </div>
  )
}

const SearchTagOptimizationsPage = () => {
  const siteId = useSiteId()
  const [status, setStatus] = useState<TagOptimizationStatus>('pending')
  const { data, isLoading, mutate } = useTagOptimizationListApi({ site_id: siteId, status })
  const showSaved = useShowSavedToast({ editing: true })

  const { trigger: run, isMutating: running } = useTagOptimizationRunApi({
    onSuccess: (r) => {
      showSaved(`検出（新規 ${r.created} 件 / 更新 ${r.updated} 件）`)
      mutate()
    }
  })
  // 処理中の「提案ID＋操作」。フック単位の isMutating で disabled を決めると、
  // 押していない行のボタンまで反応してしまう
  const [acting, setActing] = useState<{ id: number; action: 'apply' | 'dismiss' } | null>(null)
  const { trigger: apply } = useTagOptimizationApplyApi({
    onSuccess: () => {
      setActing(null)
      showSaved('提案の適用')
      mutate()
    },
    onError: () => setActing(null)
  })
  const { trigger: dismiss } = useTagOptimizationDismissApi({
    onSuccess: () => {
      setActing(null)
      showSaved('提案の見送り')
      mutate()
    },
    onError: () => setActing(null)
  })

  const counts = data?.counts_by_status ?? {}

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='最適化提案'
        description='母集団・CTR・流入をもとに、見直したほうがよいタグを週次で検出します。'
        rightNode={
          <Button
            color={ButtonColor.SECONDARY}
            loading={running}
            onClick={() => run({ site_id: siteId })}
          >
            いま検出する
          </Button>
        }
      />
      <Content>
        {data && (
          <div>
            <Text size={12} color={TextColor.SECONDARY}>
              検出の頻度: {data.batch_frequency === 'daily' ? '毎日' : '毎週（月曜）'} ／ 自動適用:{' '}
              {data.auto_apply ? 'ON' : 'OFF'}
              {data.auto_apply &&
                '（自動で実行するのは母集団の作り直しのみです。統合・表示名の変更・インデックス除外は承認が必要です）'}
            </Text>
          </div>
        )}

        <TabList>
          {STATUS_TABS.map((t) => (
            <Tab key={t.key} selected={status === t.key} onClick={() => setStatus(t.key)}>
              {t.label}
              {counts[t.key] ? `（${counts[t.key]}）` : ''}
            </Tab>
          ))}
        </TabList>

        <Area>
          <Heading>提案</Heading>
          <AreaContent>
            {isLoading || !data ? (
              <Text color={TextColor.SECONDARY}>読み込み中...</Text>
            ) : data.items.length === 0 ? (
              <div className='flex flex-col items-start gap-2'>
                <Text color={TextColor.SECONDARY}>
                  {status === 'pending'
                    ? '未処理の提案はありません。計測が貯まると、CTRや流入をもとにした提案が出ます。'
                    : '該当する提案はありません。'}
                </Text>
                {status === 'pending' && (
                  <Button color={ButtonColor.SECONDARY} link={searchTagCandidatesPath(siteId)}>
                    承認キューを開く
                  </Button>
                )}
              </div>
            ) : (
              data.items.map((o) => (
                <Row
                  key={o.id}
                  o={o}
                  siteId={siteId}
                  acting={acting?.id === o.id ? acting.action : null}
                  onApply={(id) => {
                    setActing({ id, action: 'apply' })
                    apply({ site_id: siteId, optimization_id: id })
                  }}
                  onDismiss={(id) => {
                    setActing({ id, action: 'dismiss' })
                    dismiss({ site_id: siteId, optimization_id: id })
                  }}
                />
              ))
            )}
          </AreaContent>
        </Area>
      </Content>
    </Main>
  )
}

export default SearchTagOptimizationsPage
